// src/api/botApi.js
import { apiFetch, getAuthToken } from "../apiClient";

export function getBotSettings(userId) {
    return apiFetch(`/api/bot/settings?user_id=${encodeURIComponent(userId)}`);
}

export function updateBotSettings(userId, patch) {
    return apiFetch(`/api/bot/settings?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, ...patch }),
    });
}

export function getBotStatus(userId) {
    return apiFetch(`/api/bot/status?user_id=${encodeURIComponent(userId)}`);
}

export function getBotLogs(userId, limit = 200) {
    return apiFetch(
        `/api/bot/logs?user_id=${encodeURIComponent(userId)}&limit=${limit}`
    );
}

export function runBotTick(userId) {
    return apiFetch(`/api/bot/run-tick`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}

export function killSwitch(userId, enabled = false) {
    return apiFetch(`/api/bot/kill-switch?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, enabled }),
    });
}

export function refreshBotState(userId) {
    return apiFetch(`/api/bot/state/refresh?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}

export function resetBotState(userId, clearPeak = false) {
    return apiFetch(`/api/bot/state/reset?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({
            user_id: userId,
            clear_peak: clearPeak,
        }),
    });
}
export function runSmartExit(userId, { mode = "paper", execute = false, debug = true } = {}) {
    return apiFetch(`/api/bot/smart-exit?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, mode, execute, debug }),
    });
}

// ---------------- Targeted Ticker Bot ----------------
export function getTargetBotSettings(userId) {
    return apiFetch(`/api/target-bot/settings?user_id=${encodeURIComponent(userId)}`);
}

export function updateTargetBotSettings(userId, patch) {
    return apiFetch(`/api/target-bot/settings?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, ...patch }),
    });
}

export function getTargetBotStatus(userId) {
    return apiFetch(`/api/target-bot/status?user_id=${encodeURIComponent(userId)}`);
}

export function getTargetBotLogs(userId, limit = 200) {
    return apiFetch(`/api/target-bot/logs?user_id=${encodeURIComponent(userId)}&limit=${limit}`);
}

export function runTargetBotTick(userId, { ignore_cooldown = false, execute = true, sell_only = false } = {}) {
    return apiFetch(`/api/target-bot/run-tick`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, ignore_cooldown, execute, sell_only }),
    });
}

export function resetTargetBotState(userId) {
    return apiFetch(`/api/target-bot/state/reset?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}


// ---------------- E*TRADE Broker ----------------
export function startEtradeOAuth(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/start`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, mode }),
    });
}

export function completeEtradeOAuth({ state, verifier }) {
    return apiFetch(`/api/brokers/etrade/callback`, {
        method: "POST",
        body: JSON.stringify({ state, verifier }),
    });
}

export function getEtradeStatus(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/status?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(mode)}`);
}

export function getEtradeAccount(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/account?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(mode)}`);
}

export function getEtradePositions(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/positions?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(mode)}`);
}

export function placeEtradeEquityOrder(userId, { symbol, side = "buy", qty = 1, mode = "sandbox" } = {}) {
    return apiFetch(`/api/brokers/etrade/order`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, symbol, side, qty, mode }),
    });
}

export async function getPutBotSettings() {
    return apiFetch("/api/put-bot/settings", { method: "GET" });
}

export async function savePutBotSettings(payload) {
    return apiFetch("/api/put-bot/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
    });
}

export async function getPutBotStatus() {
    return apiFetch("/api/put-bot/status", { method: "GET" });
}

export async function getPutBotLogs(limit = 100) {
    return apiFetch(`/api/put-bot/logs?limit=${encodeURIComponent(limit)}`, { method: "GET" });
}

export async function scanPutBot(limit = 10) {
    return apiFetch("/api/put-bot/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit }),
    });
}

export async function runPutBotTick({ execute = false, ignore_market_hours = false } = {}) {
    return apiFetch("/api/put-bot/run-tick", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ execute, ignore_market_hours }),
    });
}

export async function managePutBotExits({ execute = false, ignore_market_hours = false } = {}) {
    return apiFetch("/api/put-bot/manage-exits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ execute, ignore_market_hours }),
    });
}

// ---------------- Admin Diagnostics Export ----------------
const DIAGNOSTICS_API_BASE =
    process.env.REACT_APP_API_BASE || "https://mystockpicks.onrender.com";

export function getAdminDiagnosticsCatalog(userId) {
    return apiFetch(
        `/api/admin/diagnostics/catalog?user_id=${encodeURIComponent(userId || "")}`
    );
}

function diagnosticsFilenameFromDisposition(disposition) {
    const value = String(disposition || "");
    const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8?.[1]) {
        try {
            return decodeURIComponent(utf8[1].replace(/["']/g, ""));
        } catch (_) {
            return utf8[1].replace(/["']/g, "");
        }
    }
    const plain = value.match(/filename="?([^";]+)"?/i);
    return plain?.[1] || "mystockpicksai_diagnostics.zip";
}

export async function downloadAdminDiagnosticsZip(
    userId,
    {
        sections = [],
        start_date = "",
        end_date = "",
        mode = "all",
        max_rows_per_table = 10000,
    } = {}
) {
    const token = getAuthToken();
    const response = await fetch(`${DIAGNOSTICS_API_BASE}/api/admin/diagnostics/export`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            user_id: userId || "",
            sections,
            start_date: start_date || "",
            end_date: end_date || "",
            mode: mode || "all",
            max_rows_per_table,
        }),
    });

    if (!response.ok) {
        let detail = `Diagnostics export failed (${response.status})`;
        try {
            const payload = await response.json();
            detail = payload?.detail || payload?.error || detail;
        } catch (_) {
            try {
                const text = await response.text();
                if (text) detail = text.slice(0, 500);
            } catch (_) {
                // Keep the status-based message.
            }
        }
        throw new Error(detail);
    }

    const blob = await response.blob();
    return {
        blob,
        filename: diagnosticsFilenameFromDisposition(
            response.headers.get("Content-Disposition")
        ),
        size: blob.size,
    };
}

